import _ from "lodash";
import { colors } from "Constants";
import { exportData, generateRandomId } from "Utils";

export const BENCHMARK_TYPES = {
  LEARNING_OUTCOME: { label: "Learning Outcomes", color: colors.salmon60 },
  CONCEPTUAL_UNDERSTANDING: {
    label: "Conceptual Understanding",
    color: colors.blue400,
  },
};

export const isStrand = ({ depth }) => {
  return depth == 1;
};

//Applicable for new Admin Navigation
export const getPathNameForAdminNavigation = ({ activeRoute }) => {
  if (_.includes(activeRoute, "scopeandsequence")) {
    return "scopeandsequence";
  }
  if (_.includes(activeRoute, "pyp-atl")) {
    return "atl";
  }
};

export const LevelTypeMapping = {
  scopeandsequence: {
    nodeType: "BENCHMARK",
    valueType: "benchmarks",
    listEmptyText: t =>
      t("common:feed_empty_message", { label: t("common:subject_plural") }),
    listHeaderText: t => t("common:subject_plural"),
    levelStartIndex: 0,
    maxLevelLength: 3,
    showIcon: true,
    rootLevel: {
      id: "L0",
      value: "Subject",
      count: 0,
      __typename: "BenchmarkLevel",
    },
    canUpdateRootNode: false,
    rootNodeTitle: t => t("common:content_standards"),
    rootMenuItems: t => [{ label: t("common:settings"), value: "SETTINGS" }],
    levelDetails: {
      L0: {
        placeholder: t => t("common:for_example_label", { label: "Science" }),
      },
      L1: {
        placeholder: t => t("announcement:enter_title"),
      },
      L2: {
        placeholder: t => t("announcement:enter_title"),
      },
      L3: {
        placeholder: t => t("announcement:enter_title"),
      },
      default: t => t("announcement:enter_title"),
    },
    showAddBtnWhenEmpty: false,
  },
  atl: {
    nodeType: "ATL",
    valueType: "atls",
    showIcon: false,
    listEmptyText: t =>
      t("common:feed_empty_message", {
        label: t("academicSetup:skill_category_plural"),
      }),
    listHeaderText: t => t("academicSetup:skill_category_plural"),
    levelStartIndex: 1,
    maxLevelLength: 2,
    rootLevel: {
      id: "L1",
      value: "Category",
      __typename: "ATLLevel",
    },
    canUpdateRootNode: true,
    rootNodeTitle: t => t("academicSetup:skill_category"),
    rootMenuItems: t => [
      { label: t("common:settings"), value: "SETTINGS", key: "SETTINGS" },
      {
        label: t("academicSetup:define_hierarchy"),
        value: "HIERARCHY",
        key: "HIERARCHY",
      },
      { label: t("common:delete"), value: "DELETE", key: "DELETE" },
    ],
    levelDetails: {
      L0: {
        placeholder: t =>
          t("common:for_example_label", {
            label: t("academicSetup:communication_skills"),
          }),
      },
      L1: {
        placeholder: t =>
          t("common:for_example_label", {
            label: t("academicSetup:category"),
          }),
      },
      L2: {
        placeholder: t =>
          t("common:for_example_label", {
            label: t("academicSetup:skill_plural"),
          }),
      },
      L3: {
        placeholder: t =>
          t("common:for_example_label", {
            label: t("academicSetup:sub_skill_plural"),
          }),
      },
    },
    showAddBtnWhenEmpty: true,
  },
};

export const headerDataMapping = {
  showSubjects: ({
    t,
    loginActiveTab,
    isDynamicPlannerElementLabel,
    plannerElementSetLabel,
  }) => ({
    headerTitle: isDynamicPlannerElementLabel
      ? plannerElementSetLabel
      : t("scopeAndSequence:scope_and_sequence"),
    headerDescription:
      loginActiveTab == "teacher"
        ? t("scopeAndSequence:define_scope_sequence_teacher")
        : t("scopeAndSequence:define_scope_sequence"),
    headerDescriptionStyle: { fontSize: "18px", marginBottom: 32 },
  }),
  showMenu: ({ plannerElementSetLabel, subjectLabel, t }) => ({
    headerTitle:
      (subjectLabel ? `${subjectLabel} ` : "") + plannerElementSetLabel,
    headerDescription: t("scopeAndSequence:menu_header_description"),
    headerDescriptionStyle: { fontSize: "16px", marginBottom: "68px" },
  }),
  showSelection: ({ subjectLabel, t }) => ({
    headerTitle: t("scopeAndSequence:pick_from_set"),
    headerDescription: t("scopeAndSequence:import_sns_header_description", {
      subjectLabel,
    }),
    headerDescriptionStyle: {
      marginBottom: "44px",
    },
  }),
  showUpload: ({ t }) => ({
    headerTitle: t("scopeAndSequence:bulk_upload_title"),
    headerDescription: t("scopeAndSequence:upload_description"),
    headerDescriptionStyle: {
      marginBottom: "64px",
    },
  }),
  showUploadReconfigure: ({ plannerElementSetLabel, subjectLabel, t }) => ({
    headerTitle: t("scopeAndSequence:append_item_module", {
      itemName: subjectLabel,
      moduleName: plannerElementSetLabel,
    }),
    headerDescription: t("scopeAndSequence:append_module_sns_description", {
      moduleName: plannerElementSetLabel,
    }),
    headerDescriptionStyle: {
      marginBottom: "64px",
    },
  }),

  showEditLevels: ({ plannerElementSetLabel, subjectLabel, t }) => ({
    headerTitle: t("scopeAndSequence:start_define_module_hierarchy", {
      moduleName: plannerElementSetLabel,
    }),
    headerDescription: t("scopeAndSequence:define_max_levels_for_item_module", {
      itemName: subjectLabel,
      moduleName: plannerElementSetLabel,
    }),
    headerDescriptionStyle: {
      marginBottom: "64px",
    },
  }),
  showEditLevelsV2: ({ plannerElementSetLabel, subjectLabel, t }) => ({
    headerTitle: t("scopeAndSequence:define_hierarchy_for_module", {
      moduleName: plannerElementSetLabel,
    }),
    headerDescription: t("scopeAndSequence:rename_level_for_item_module", {
      itemName: subjectLabel,
      moduleName: plannerElementSetLabel,
    }),
    headerDescriptionStyle: {
      marginBottom: "64px",
    },
  }),
};

export const onDownloadTemplateClick = async ({
  subjectId,
  fileName,
  getSubjectSnSTemplateData,
  nodeType,
  setProgress,
  removeProgressElement,
}) => {
  const updatedFileName = `${_.snakeCase(fileName)}_upload_template`;
  const attachmentId = generateRandomId();
  setProgress({ fileName: updatedFileName, attachmentId });
  const result = await getSubjectSnSTemplateData(subjectId, nodeType);
  removeProgressElement({ id: attachmentId });
  const snsTemplateData = result.data.snsUploadTemplate;
  exportData({
    data: snsTemplateData,
    outputFormat: "XLSX",
    fileName: updatedFileName,
  });
};

export const onDownloadExistingSnsClick = async ({
  subjectId,
  nodeType,
  fileName,
  plannerElementSetId,
  getSubjectExistingSnSTemplateData,
  setProgress,
  removeProgressElement,
}) => {
  const attachmentId = generateRandomId();
  setProgress({ fileName, attachmentId });
  const result = await getSubjectExistingSnSTemplateData({
    subjectId,
    nodeType,
    plannerElementSetId,
  });
  removeProgressElement({ id: attachmentId });
  const snsTemplateData = _.get(result, "data.yearData", {});
  snsTemplateData.filename = fileName;

  exportData({
    data: snsTemplateData,
    outputFormat: "XLSX",
    fileName: snsTemplateData.filename,
  });
};
